import type { PolicyEntityT } from "../../domain/entities/policy.entity";
import type {
  UpdatePermissionPolicyRequestDtoT,
  UpdateVacationPolicyRequestDtoT,
} from "../../domain/dtos/policy.dto";
import type {
  PolicyMapperT,
  UpdatePolicyResponseT,
} from "../types/policy.types";
import type {
  PermissionPolicyFormValuesT,
  VacationPolicyFormValuesT,
} from "../../domain/types/policy.types";

export const policyMapper: PolicyMapperT = {
  toPermissionPolicyDTO: (
    formValues: PermissionPolicyFormValuesT,
  ): UpdatePermissionPolicyRequestDtoT => {
    let minAdvanceNoticeDays = null;

    if (!formValues.isJustified) {
      minAdvanceNoticeDays =
        formValues.advanceNoticeRequirement === "required"
          ? formValues.minAdvanceNoticeDays
          : 0;
    }

    let maxBackdateDays = null;

    if (!formValues.isJustified) {
      maxBackdateDays =
        formValues.allowBackdateDays === "yes" ? formValues.maxBackdateDays : 0;
    }

    return {
      approval_flow: formValues.approvalFlow ?? "leader_rrhh",
      requires_attachment: formValues.requiresAttachment === "yes",
      min_advance_notice_days: minAdvanceNoticeDays,
      max_backdate_days: maxBackdateDays,
      max_advance_days: formValues.maxAdvanceDays ?? 0,
      permission_policy: {
        has_balance_control: formValues.hasBalanceControl === "yes",
        max_days_per_request: formValues.maxDaysPerRequest ?? null,
        annual_days: formValues.annualDays ?? null,
        reset_month: formValues.resetMonth ?? null,
      },
    };
  },
  toVacationPolicyDTO: (
    formValues: VacationPolicyFormValuesT,
  ): UpdateVacationPolicyRequestDtoT => ({
    approval_flow: formValues.approvalFlow ?? null,
    requires_attachment: formValues.requiresAttachment ?? false,
    min_advance_notice_days: formValues.minAdvanceNoticeDays ?? null,
    max_backdate_days: formValues.maxBackdateDays ?? null,
    max_advance_days: formValues.maxAdvanceDays ?? null,
    vacation_policy: {
      minimum_eligibility_days: formValues.minimumEligibilityDays,
      accrual_mode: formValues.accrualMode,
      allow_negative_balance: formValues.allowNegativeBalance === "yes",
      max_negative_balance_days: formValues.maxNegativeBalanceDays ?? null,
    },
  }),
  updatePolicyToDomain: (rawData: UpdatePolicyResponseT): PolicyEntityT => {
    const policy = rawData?.data;

    if (!policy) {
      throw new Error(
        "Ocurrió un error al actualizar la política de vacaciones",
      );
    }

    return {
      id: policy.id,
      accrualMode: policy.accrual_mode ?? null,
      active: policy.active ?? null,
      allowNegativeBalance: policy.allow_negative_balance ?? null,
      annualDays: policy.annual_days ?? null,
      approvalFlow: policy.approval_flow ?? null,
      hasBalanceControl: policy.has_balance_control ?? null,
      maxAdvanceDays: policy.max_advance_days ?? null,
      maxBackdateDays: policy.max_backdate_days ?? null,
      maxDaysPerRequest: policy.max_days_per_request ?? null,
      maxNegativeBalanceDays: policy.max_negative_balance_days ?? null,
      minAdvanceNoticeDays: policy.min_advance_notice_days ?? null,
      minimumEligibilityDays: policy.minimum_eligibility_days ?? null,
      requiresAttachment: policy.requires_attachment ?? null,
      resetMonth: policy.reset_month ?? null,
    };
  },
};
